import { supabase } from './supabase';

export interface NotificationPreference {
  id: string;
  patient_id: string;
  enable_sms: boolean;
  enable_email: boolean;
  sms_number: string | null;
  email_address: string | null;
  created_at: string;
  updated_at: string;
}

export interface Notification {
  id: string;
  patient_id: string;
  notification_type: 'sms' | 'email';
  channel: string;
  recipient: string;
  subject?: string;
  message: string;
  status: 'pending' | 'sent' | 'failed' | 'delivered';
  error_message?: string;
  metadata?: any;
  sent_at?: string;
  delivered_at?: string;
  created_at: string;
  updated_at: string;
}

class NotificationPreferenceService {
  async getByPatient(patientId: string): Promise<NotificationPreference | null> {
    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('patient_id', patientId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return data;
  }

  async createOrUpdate(patientId: string, preferences: Partial<NotificationPreference>): Promise<NotificationPreference> {
    const existing = await this.getByPatient(patientId);

    if (existing) {
      const { data, error } = await supabase
        .from('notification_preferences')
        .update(preferences)
        .eq('patient_id', patientId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } else {
      const { data, error } = await supabase
        .from('notification_preferences')
        .insert({ patient_id: patientId, ...preferences })
        .select()
        .single();

      if (error) throw error;
      return data;
    }
  }

  async getAll(): Promise<NotificationPreference[]> {
    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }
}

class NotificationService {
  async getAll(filters?: { patient_id?: string; status?: string }): Promise<Notification[]> {
    let query = supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters?.patient_id) {
      query = query.eq('patient_id', filters.patient_id);
    }

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  }

  async getById(id: string): Promise<Notification | null> {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return data;
  }

  async create(notification: Omit<Notification, 'id' | 'created_at' | 'updated_at'>): Promise<Notification> {
    const { data, error } = await supabase
      .from('notifications')
      .insert(notification)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateStatus(
    id: string,
    status: 'pending' | 'sent' | 'failed' | 'delivered',
    errorMessage?: string
  ): Promise<Notification> {
    const update: any = { status };
    
    if (status === 'sent') {
      update.sent_at = new Date().toISOString();
    }
    
    if (status === 'delivered') {
      update.delivered_at = new Date().toISOString();
    }
    
    if (errorMessage) {
      update.error_message = errorMessage;
    }

    const { data, error } = await supabase
      .from('notifications')
      .update(update)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async sendSMS(patientId: string, phoneNumber: string, message: string, metadata?: any): Promise<Notification> {
    const notification = await this.create({
      patient_id: patientId,
      notification_type: 'sms',
      channel: 'twilio',
      recipient: phoneNumber,
      message,
      status: 'pending',
      metadata: metadata || {},
    });

    try {
      const twilioAccountSid = (import.meta.env.VITE_TWILIO_ACCOUNT_SID as string);
      const twilioAuthToken = (import.meta.env.VITE_TWILIO_AUTH_TOKEN as string);
      const twilioPhoneNumber = (import.meta.env.VITE_TWILIO_PHONE_NUMBER as string);

      if (!twilioAccountSid || !twilioAuthToken || !twilioPhoneNumber) {
        console.warn('Twilio credentials not configured. SMS will be logged but not sent.');
        console.log(`[SMS Mock] To: ${phoneNumber}, Message: ${message}`);
        await this.updateStatus(notification.id, 'sent');
        return notification;
      }

      const response = await fetch(
        `https://api.twilio.com/2010-04-01/Accounts/${twilioAccountSid}/Messages.json`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': 'Basic ' + btoa(`${twilioAccountSid}:${twilioAuthToken}`),
          },
          body: new URLSearchParams({
            To: phoneNumber,
            From: twilioPhoneNumber,
            Body: message,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send SMS');
      }

      const result = await response.json();
      await this.updateStatus(notification.id, 'sent');
      
      return notification;
    } catch (error: any) {
      console.error('Failed to send SMS:', error);
      await this.updateStatus(notification.id, 'failed', error.message);
      throw error;
    }
  }

  async sendEmail(
    patientId: string,
    emailAddress: string,
    subject: string,
    message: string,
    metadata?: any
  ): Promise<Notification> {
    const notification = await this.create({
      patient_id: patientId,
      notification_type: 'email',
      channel: 'supabase',
      recipient: emailAddress,
      subject,
      message,
      status: 'pending',
      metadata: metadata || {},
    });

    try {
      const supabaseServiceUrl = (import.meta.env.VITE_SUPABASE_URL as string);
      const supabaseServiceKey = (import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY as string);

      if (!supabaseServiceUrl || !supabaseServiceKey) {
        console.warn('Supabase service credentials not configured. Email will be logged but not sent.');
        console.log(`[Email Mock] To: ${emailAddress}, Subject: ${subject}, Message: ${message}`);
        await this.updateStatus(notification.id, 'sent');
        return notification;
      }

      const response = await fetch(`${supabaseServiceUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseServiceKey}`,
        },
        body: JSON.stringify({
          to: emailAddress,
          subject,
          html: message,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send email');
      }

      await this.updateStatus(notification.id, 'sent');
      
      return notification;
    } catch (error: any) {
      console.error('Failed to send email:', error);
      await this.updateStatus(notification.id, 'failed', error.message);
      throw error;
    }
  }

  async notifyResultReady(
    patientId: string,
    patientName: string,
    orderType: 'lab' | 'radiology',
    orderNo: string
  ): Promise<void> {
    const preferences = await notificationPreferenceService.getByPatient(patientId);
    
    if (!preferences) {
      console.warn(`No notification preferences found for patient ${patientId}`);
      return;
    }

    const moduleLabel = orderType === 'lab' ? 'Laboratory' : 'Radiology';
    const smsMessage = `Dear ${patientName}, your ${moduleLabel} test results for order ${orderNo} are ready. Please visit the hospital to collect your report.`;
    const emailSubject = `${moduleLabel} Test Results Ready - ${orderNo}`;
    const emailMessage = `
      <html>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
          <h2 style="color: #2563eb;">Test Results Ready</h2>
          <p>Dear ${patientName},</p>
          <p>Your ${moduleLabel} test results are now ready.</p>
          <div style="background-color: #f3f4f6; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <p style="margin: 5px 0;"><strong>Order Number:</strong> ${orderNo}</p>
            <p style="margin: 5px 0;"><strong>Test Type:</strong> ${moduleLabel}</p>
          </div>
          <p>Please visit the hospital to collect your report.</p>
          <p style="margin-top: 30px; color: #666; font-size: 12px;">
            This is an automated notification from the Hospital Management System.
          </p>
        </body>
      </html>
    `;

    const promises: Promise<any>[] = [];

    if (preferences.enable_sms && preferences.sms_number) {
      promises.push(
        this.sendSMS(patientId, preferences.sms_number, smsMessage, {
          order_type: orderType,
          order_no: orderNo,
        }).catch(error => {
          console.error('Failed to send SMS notification:', error);
        })
      );
    }

    if (preferences.enable_email && preferences.email_address) {
      promises.push(
        this.sendEmail(patientId, preferences.email_address, emailSubject, emailMessage, {
          order_type: orderType,
          order_no: orderNo,
        }).catch(error => {
          console.error('Failed to send email notification:', error);
        })
      );
    }

    await Promise.allSettled(promises);
  }
}

export const notificationPreferenceService = new NotificationPreferenceService();
export const notificationService = new NotificationService();
